import {
  ValidationQuestion,
  ValidationQuestionData,
  ValidationQuestionDataItem,
} from "../interfaces/document-category.interface";
import {
  ControlConfig,
  GenericOption,
  TableData,
  TableDataColumn,
} from "../interfaces/validation-question-table.interface";
import {
  extractFormValueFromAnswer,
  hasMapCaptureEvidence,
  hasStoredQuestionAnswer,
} from "./question-stepper-answer-display.util";

export type OverviewTableCellType = "text" | "status-tag" | "empty";

export interface OverviewTableCellDisplay {
  type: OverviewTableCellType;
  value?: string;
  label?: string;
  color?: string;
  soft?: boolean;
  tagType?: string;
  icon?: string;
  size?: string;
}

export interface OverviewTableColumnDisplay {
  name: string;
  header: string;
  align?: string;
  width?: string;
}

export interface OverviewTableDisplay {
  title?: string;
  description?: string;
  columns: OverviewTableColumnDisplay[];
  rows: Record<string, OverviewTableCellDisplay>[];
}

export interface OverviewQuestionDataItemDisplay {
  title?: string;
  subtitle?: string;
  value?: string;
  description?: string;
}

export interface OverviewQuestionDataDisplay {
  title?: string;
  items: OverviewQuestionDataItemDisplay[];
}

function isCellObject(cell: unknown): cell is Record<string, unknown> {
  return cell != null && typeof cell === "object";
}

function hasNonEmptyString(value: unknown): boolean {
  return typeof value === "string" && value.trim() !== "";
}

function findOptionLabel(options: GenericOption[] | undefined, selectedValue: string): string | undefined {
  return options?.find((option) => option.value === selectedValue)?.label;
}

function resolveDropdownDisplayValue(cell: ControlConfig, column: TableDataColumn | undefined): string | undefined {
  const selectedValue = extractFormValueFromAnswer(cell.answer);
  if (selectedValue == null || selectedValue === "") {
    return hasNonEmptyString(cell.value) ? String(cell.value).trim() : undefined;
  }

  const options = cell.options ?? column?.control_config?.options;
  const label = findOptionLabel(options, String(selectedValue));
  return label ?? String(selectedValue);
}

/** Returns true when a table cell has a resolvable display value or saved answer. */
export function hasOverviewTableCellDisplayEvidence(cell: unknown, column: TableDataColumn | undefined): boolean {
  if (!isCellObject(cell)) {
    return false;
  }

  const cellType = (cell.row_type as string | undefined) ?? column?.type;

  if (cellType === "text" && hasNonEmptyString(cell.value)) {
    return true;
  }

  if (cellType === "status-tag" && hasNonEmptyString(cell.label)) {
    return true;
  }

  if (cellType === "dropdown") {
    const selectedValue = extractFormValueFromAnswer((cell as ControlConfig).answer);
    if (selectedValue != null && selectedValue !== "") {
      return true;
    }
    return hasNonEmptyString(cell.value);
  }

  return hasNonEmptyString(cell.value);
}

/** Returns true when a question has table rows with displayable cell evidence. */
export function hasTableDisplayEvidence(question: Partial<ValidationQuestion> | null | undefined): boolean {
  if (!question?.tables?.length) {
    return false;
  }

  return question.tables.some((table) =>
    (table.data ?? []).some((row) =>
      (table.columns ?? []).some((column) => hasOverviewTableCellDisplayEvidence(row[column.name], column))
    )
  );
}

/** Returns true when a question should appear in read-only overview displays. */
export function hasOverviewQuestionDisplayEvidence(question: Partial<ValidationQuestion> | null | undefined): boolean {
  return hasStoredQuestionAnswer(question) || hasMapCaptureEvidence(question) || hasTableDisplayEvidence(question);
}

/** Normalizes a table cell into a read-only overview display shape. */
export function resolveOverviewTableCellDisplay(
  cell: unknown,
  column: TableDataColumn | undefined
): OverviewTableCellDisplay {
  if (!isCellObject(cell)) {
    return { type: "empty" };
  }

  const cellType = (cell.row_type as string | undefined) ?? column?.type;

  if (cellType === "dropdown") {
    const displayValue = resolveDropdownDisplayValue(cell as ControlConfig, column);
    if (!displayValue) {
      return { type: "empty" };
    }

    return { type: "text", value: displayValue };
  }

  if (cellType === "status-tag") {
    return {
      type: "status-tag",
      label: String(cell.label ?? ""),
      color: (cell.color as string | undefined) ?? column?.tag_config?.color ?? "primary",
      soft: (cell.soft as boolean | undefined) ?? column?.tag_config?.soft ?? false,
      tagType: (cell.type as string | undefined) ?? column?.tag_config?.type ?? "default",
      icon: (cell.icon as string | undefined) ?? column?.tag_config?.icon,
      size: (cell.size as string | undefined) ?? column?.tag_config?.size ?? "md",
    };
  }

  if (hasNonEmptyString(cell.value)) {
    return { type: "text", value: String(cell.value) };
  }

  return { type: "empty" };
}

function buildOverviewDisplayTable(table: TableData): OverviewTableDisplay | null {
  const candidateColumns = table.columns ?? [];
  if (!candidateColumns.length) {
    return null;
  }

  const rows = (table.data ?? []).map((row) => {
    const displayRow: Record<string, OverviewTableCellDisplay> = {};
    for (const column of candidateColumns) {
      displayRow[column.name] = resolveOverviewTableCellDisplay(row[column.name], column);
    }
    return displayRow;
  });

  const columns = candidateColumns.filter((column) => {
    if (column.header?.trim()) {
      return true;
    }

    return rows.some((row) => row[column.name]?.type !== "empty");
  });

  if (!columns.length) {
    return null;
  }

  const hasVisibleRows = rows.some((row) => columns.some((column) => row[column.name]?.type !== "empty"));

  if (!hasVisibleRows) {
    return null;
  }

  return {
    title: table.title?.trim() || undefined,
    description: table.description?.message?.trim() || undefined,
    columns: columns.map((column) => ({
      name: column.name,
      header: column.header?.trim() || "",
      align: column.align,
      width: column.width,
    })),
    rows,
  };
}

/** Transforms question tables into a read-only overview display model. */
export function buildOverviewDisplayTables(
  question: Partial<ValidationQuestion> | null | undefined
): OverviewTableDisplay[] {
  if (!question?.tables?.length) {
    return [];
  }

  return question.tables
    .map((table) => buildOverviewDisplayTable(table))
    .filter((table): table is OverviewTableDisplay => table != null);
}

/** Resolves overview instructions without mixing them into question text. */
export function resolveOverviewInstructions(
  question: Partial<ValidationQuestion> | null | undefined
): { title: string; message: string } | null {
  if (!question) {
    return null;
  }

  const instructionTitle = question.instructions?.title?.trim();
  const instructionMessage = question.instructions?.message?.trim();
  if (instructionTitle || instructionMessage) {
    return {
      title: instructionTitle || "Instrucciones:",
      message: instructionMessage || "",
    };
  }

  const descriptionInstructions = question.question_description?.instructions;
  const descriptionTitle = descriptionInstructions?.title?.trim();
  const descriptionMessage = descriptionInstructions?.message?.trim();
  if (descriptionTitle || descriptionMessage) {
    return {
      title: descriptionTitle || "Instrucciones:",
      message: descriptionMessage || "",
    };
  }

  const header = question.header;
  if (header) {
    const headerTitle = header.title?.trim() || "";
    const headerSubtitle = header.subtitle?.trim() || "";
    const headerDescription = header.description?.trim() || "";

    if (headerTitle || headerSubtitle || headerDescription) {
      const isInstructionSubtitle = /^instrucc?i[oó]n(es)?:?$/i.test(headerSubtitle);

      // Common economic/GRS pattern: header.title + header.description under an "Instrucción(es)" subtitle.
      if (isInstructionSubtitle) {
        return {
          title: headerTitle || headerSubtitle || "Instrucciones:",
          message: headerDescription,
        };
      }

      // Header used as instruction content when there is no scripted question text.
      if (!question.formatted_question?.trim() && (headerTitle || headerDescription)) {
        return {
          title: headerTitle || headerSubtitle || "Instrucciones:",
          message: headerDescription,
        };
      }

      if (headerSubtitle || headerDescription) {
        return {
          title: headerSubtitle || "Instrucciones:",
          message: headerDescription,
        };
      }
    }
  }

  // Follow-up fields (e.g. textarea) may expose instructional copy in `message`
  // while `title`/`label` carry the question heading.
  const fallbackMessage = typeof question.message === "string" ? question.message.trim() : "";
  if (!fallbackMessage) {
    return null;
  }

  const hasOtherDisplayText = !!(
    question.formatted_question?.trim() ||
    question.question?.trim() ||
    question.header?.title?.trim() ||
    (typeof question.title === "string" && question.title.trim()) ||
    (typeof question.label === "string" && question.label.trim())
  );

  if (!hasOtherDisplayText) {
    return null;
  }

  return {
    title: "Instrucciones:",
    message: fallbackMessage,
  };
}

/**
 * Resolves script/question text for overview displays.
 * Avoids falling back to header/instructions fields that belong in the instructions block.
 */
export function resolveOverviewQuestionDisplayText(question: Partial<ValidationQuestion> | null | undefined): string {
  if (!question) {
    return "";
  }

  return question.formatted_question?.trim() || question.question?.trim() || "";
}

function mapQuestionDataItem(
  item: ValidationQuestionDataItem | null | undefined
): OverviewQuestionDataItemDisplay | null {
  if (!item) {
    return null;
  }

  const title = item.title?.trim() || undefined;
  const subtitle = item.subtitle?.trim() || undefined;
  const value = item.value != null && String(item.value).trim() !== "" ? String(item.value) : undefined;
  const description = item.description?.trim() || undefined;

  if (!title && !subtitle && !value && !description) {
    return null;
  }

  return { title, subtitle, value, description };
}

function collectQuestionDataItems(data: ValidationQuestionData): OverviewQuestionDataItemDisplay[] {
  const fromItems = (data.items ?? [])
    .map((item) => mapQuestionDataItem(item))
    .filter((item): item is OverviewQuestionDataItemDisplay => item != null);

  const fromTables = (data.tables ?? []).flatMap((table) =>
    (table.items ?? [])
      .map((item) => mapQuestionDataItem(item))
      .filter((item): item is OverviewQuestionDataItemDisplay => item != null)
  );

  return [...fromItems, ...fromTables];
}

/** Transforms question_data cards into a read-only overview display model. */
export function buildOverviewDisplayQuestionData(
  question: Partial<ValidationQuestion> | null | undefined
): OverviewQuestionDataDisplay[] {
  if (!question?.question_data?.length) {
    return [];
  }

  return question.question_data
    .map((data): OverviewQuestionDataDisplay | null => {
      const items = collectQuestionDataItems(data);
      if (!items.length && !data.title?.trim()) {
        return null;
      }

      return {
        title: data.title?.trim() || undefined,
        items,
      };
    })
    .filter((data): data is OverviewQuestionDataDisplay => data != null);
}

function resolveOptionLabelFromQuestion(
  question: Partial<ValidationQuestion>,
  selectedValue: unknown
): string | undefined {
  if (selectedValue == null || selectedValue === "") {
    return undefined;
  }

  const options = question.options as GenericOption[] | undefined;
  return findOptionLabel(options, String(selectedValue));
}

const CHECKBOX_TRUE_VALUES = new Set<unknown>([true, "true", 1, "1"]);
const CHECKBOX_FALSE_VALUES = new Set<unknown>([false, "false", 0, "0"]);

function resolveCheckboxDisplayValue(extracted: unknown): string | null {
  if (CHECKBOX_TRUE_VALUES.has(extracted)) {
    return "Sí";
  }
  if (CHECKBOX_FALSE_VALUES.has(extracted)) {
    return "No";
  }
  return null;
}

function resolveMultiValueAnswerDisplay(question: Partial<ValidationQuestion>, values: unknown[]): string | null {
  const labels = values
    .map((value) => resolveOptionLabelFromQuestion(question, value) ?? String(value))
    .filter((value) => value.trim() !== "");

  return labels.length ? labels.join(", ") : null;
}

function resolveSingleAnswerDisplayValue(question: Partial<ValidationQuestion>, extracted: unknown): string {
  return resolveOptionLabelFromQuestion(question, extracted) ?? String(extracted);
}

/** Resolves a human-readable answer for overview displays (option labels, checkboxes, raw values). */
export function resolveOverviewAnswerDisplayValue(
  question: Partial<ValidationQuestion> | null | undefined
): string | null {
  if (!question) {
    return null;
  }

  const extracted = extractFormValueFromAnswer(question.answer, { question });
  if (extracted == null || extracted === "") {
    return null;
  }

  if (question.type === "checkbox") {
    const checkboxValue = resolveCheckboxDisplayValue(extracted);
    if (checkboxValue != null) {
      return checkboxValue;
    }
  }

  if (Array.isArray(extracted)) {
    return resolveMultiValueAnswerDisplay(question, extracted);
  }

  return resolveSingleAnswerDisplayValue(question, extracted);
}

const OVERVIEW_FIELD_COMPLEMENT_TYPES = new Set(["dropdown", "input", "textarea", "checkbox"]);

/**
 * Returns true when a follow-up is a form field complement of a parent answer
 * (e.g. Marca/Modelo/Año), not a standalone scripted question.
 */
export function isOverviewFieldComplement(question: Partial<ValidationQuestion> | null | undefined): boolean {
  if (!question?.type || !OVERVIEW_FIELD_COMPLEMENT_TYPES.has(question.type)) {
    return false;
  }

  // Nested scripted questions always expose formatted_question.
  return !question.formatted_question?.trim();
}

export interface OverviewAnswerComplement {
  label: string;
  value: string;
}

function resolveComplementLabel(followUp: Partial<ValidationQuestion>): string {
  if (typeof followUp.label === "string" && followUp.label.trim()) {
    return followUp.label.trim();
  }
  if (typeof followUp.title === "string" && followUp.title.trim()) {
    return followUp.title.trim();
  }
  if (typeof followUp.control === "string" && followUp.control.trim()) {
    return followUp.control.trim();
  }
  return "Detalle";
}

function resolveOptionComparableValue(optionValue: unknown): unknown {
  if (optionValue != null && typeof optionValue === "object" && "value" in (optionValue as Record<string, unknown>)) {
    return (optionValue as { value: unknown }).value;
  }
  return optionValue;
}

function isParentOptionSelected(question: Partial<ValidationQuestion>, optionValue: unknown): boolean {
  const selected = extractFormValueFromAnswer(question.answer, { question });
  if (selected == null) {
    return false;
  }
  const comparable = resolveOptionComparableValue(optionValue);
  return selected === comparable || selected === optionValue;
}

/** Collects answered field-style follow-ups for the selected parent option. */
export function resolveOverviewAnswerComplements(
  question: Partial<ValidationQuestion> | null | undefined
): OverviewAnswerComplement[] {
  if (!question?.options?.length) {
    return [];
  }

  const complements: OverviewAnswerComplement[] = [];

  for (const option of question.options) {
    const followUps = option.follow_up_questions as Partial<ValidationQuestion>[] | undefined;
    if (!followUps?.length) {
      continue;
    }

    if (hasStoredQuestionAnswer(question) && !isParentOptionSelected(question, option.value)) {
      continue;
    }

    for (const followUp of followUps) {
      if (!isOverviewFieldComplement(followUp)) {
        continue;
      }

      const value = resolveOverviewAnswerDisplayValue(followUp);
      if (!value) {
        continue;
      }

      complements.push({
        label: resolveComplementLabel(followUp),
        value,
      });
    }
  }

  return complements;
}

/**
 * When Marca/Modelo/Año complements are present, collapse them into a single summary line.
 * Returns null when complements should be listed individually.
 */
export function formatOverviewAnswerComplementSummary(
  complements: OverviewAnswerComplement[] | null | undefined
): string | null {
  if (!complements?.length) {
    return null;
  }

  const findByLabel = (pattern: RegExp): OverviewAnswerComplement | undefined =>
    complements.find((item) => pattern.test(item.label.trim()));

  const marca = findByLabel(/^marca$/i);
  const modelo = findByLabel(/^modelo$/i);
  const anio = findByLabel(/^a[ñn]o$/i);

  if (!marca || !modelo || !anio) {
    return null;
  }

  // Only collapse when the complement set is exactly the vehicle fields.
  if (complements.length !== 3) {
    return null;
  }

  return `Vehículo correcto: ${marca.value} ${modelo.value} ${anio.value}`;
}
